import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Award, ExternalLink, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import SelectionBar from '@/components/ui/selection-bar'
import BadgeCard from '@/components/feature/BadgeCard'
import BadgeCreateSheet from '@/components/feature/BadgeCreateSheet'
import { deleteBadge, getBadges } from '@/api/badges'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { OpenBadge } from '@/types'

export default function BadgeTab() {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedBadge, setSelectedBadge] = useState<OpenBadge | null>(null)
  const selection = useSelectionMode()
  const isPC = useMediaQuery('(min-width: 768px)')

  const { data: badges = [], isLoading } = useQuery({
    queryKey: ['badges'],
    queryFn: getBadges,
  })

  const removeBadge = useMutation({
    mutationFn: deleteBadge,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['badges'] })
      toast.success('削除しました')
    },
  })

  const handleBulkDelete = async () => {
    await Promise.all(selection.selectedIds.map((id) => deleteBadge(id)))
    qc.invalidateQueries({ queryKey: ['badges'] })
    toast.success(`${selection.selectedIds.length}件を削除しました`)
    selection.clear()
  }

  return (
    <div>
      {isPC && !selection.isSelecting && badges.length > 0 && (
        <div className="flex justify-end mb-3">
          <Button variant="ghost" size="sm" onClick={selection.enter}>選択</Button>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">読み込み中...</div>
      ) : badges.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <p className="text-sm">バッジがありません</p>
          <p className="text-xs mt-1">右下の＋から登録してください</p>
        </div>
      ) : (
        <div className="space-y-3">
          {badges.map((badge) => (
            <BadgeCard
              key={badge.id}
              badge={badge}
              onClick={selection.isSelecting ? undefined : () => setSelectedBadge(badge)}
              onDelete={(id) => removeBadge.mutate(id)}
              isSelecting={selection.isSelecting}
              isSelected={selection.selectedIds.includes(badge.id)}
              onSelect={selection.toggle}
              onLongPress={!isPC ? selection.enter : undefined}
            />
          ))}
        </div>
      )}

      {!selection.isSelecting && (
        <Button onClick={() => setCreateOpen(true)} size="icon"
          className="fixed bottom-20 right-4 h-12 w-12 rounded-full shadow-lg">
          <Plus size={24} />
        </Button>
      )}

      {selection.isSelecting && (
        <SelectionBar
          count={selection.selectedIds.length}
          onDelete={handleBulkDelete}
          onCancel={selection.clear}
        />
      )}

      <BadgeCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />

      <Dialog open={!!selectedBadge} onOpenChange={(v) => { if (!v) setSelectedBadge(null) }}>
        <DialogContent>
          {selectedBadge && (
            <>
              <DialogHeader>
                <DialogTitle>バッジ詳細</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col items-center gap-4">
                {selectedBadge.badge_image_url ? (
                  <img src={selectedBadge.badge_image_url} alt={selectedBadge.badge_name}
                    className="w-24 h-24 rounded-lg object-contain" />
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center">
                    <Award size={40} className="text-muted-foreground" />
                  </div>
                )}
                <div className="w-full space-y-1 text-center">
                  <p className="text-lg font-semibold">{selectedBadge.badge_name || 'バッジ'}</p>
                  {selectedBadge.issuer_name && (
                    <p className="text-sm text-muted-foreground">{selectedBadge.issuer_name}</p>
                  )}
                  {selectedBadge.issued_on && (
                    <p className="text-sm text-muted-foreground">
                      {new Date(selectedBadge.issued_on).toLocaleDateString('ja-JP')} 取得
                    </p>
                  )}
                </div>
                {selectedBadge.badge_url && (
                  <a href={selectedBadge.badge_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-xs text-primary underline underline-offset-2 max-w-full">
                    <ExternalLink size={12} className="shrink-0" />
                    <span className="truncate">{selectedBadge.badge_url}</span>
                  </a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
